import React from "react";
import StarRating from "./StarRating";
import { fmtAvg } from "../format";
import { REVIEWS } from "../../constants/testIds/reviews";

/**
 * The one-line rating a marketplace card carries: ★ 4.3 (12).
 *
 * THREE ANSWERS, NOT TWO.
 *
 *   · NO `rating` KEY AT ALL → render nothing. That is not "this practice has no reviews",
 *     it is "this payload predates the reviews module" (a cached list, a degraded response,
 *     a backend not yet deployed). A badge invented from an absent key would state a fact
 *     the server never sent; silence is the only honest answer, and it is also what keeps
 *     `MarketplaceLearn` working unchanged against an older API.
 *   · `review_count === 0` → the NEW badge. A practice with no review must not read "0.0"
 *     or "—★": zero reviews is not a bad rating, and a five-star scale anchored at zero is
 *     exactly how a new practice gets buried. It says "new" and shows no stars.
 *   · otherwise → the average to ONE decimal (the contract's display rule: the server
 *     rounds to two) with its count.
 *
 * The visible number and the stars are the same fact twice, so the badge carries ONE
 * sentence for a screen reader and every glyph and numeral inside it is hidden.
 *
 * `role="img"` IS WHAT MAKES THAT SENTENCE AUDIBLE. `aria-label` on a bare `<span>` labels
 * an element whose implicit role is `generic`, and a generic element is not a labellable
 * one — browsers prune the name, so the badge read out as nothing at all while every
 * visible part of it was hidden from the very reader the label was written for. A role
 * that accepts a name fixes it, and `img` is the honest one: this is one glanceable mark,
 * not a group of controls.
 *
 * @param {?object} rating a `rating` sub-document `{avg_rating, review_count, rank_score}`
 * @param {string} tenantId the card's tenant, for the test id
 * @param {function} t `useT("marketplace")`
 * @param {"dark"|"light"} [variant]
 */
export default function RatingBadge({ rating, tenantId, t, variant = "dark" }) {
  if (!rating || typeof rating !== "object") return null;

  const count = Number(rating.review_count) || 0;
  const muted = variant === "dark" ? "text-white/40" : "text-eh-muted";

  if (count === 0) {
    return (
      <span
        data-testid={REVIEWS.cardRatingNew(tenantId)}
        className={`inline-flex items-center text-[11px] mt-1.5 ${muted}`}
      >
        {t("rating.new")}
      </span>
    );
  }

  const avg = fmtAvg(rating.avg_rating);
  return (
    <span
      role="img"
      data-testid={REVIEWS.cardRating(tenantId)}
      className="inline-flex items-center gap-1.5 mt-1.5"
      aria-label={t("rating.badgeAria", { avg, count })}
    >
      {/* Hidden EXPLICITLY, not by handing `StarRating` an empty `label`: an unnamed
          `role="img"` is still an image in the tree, and some readers announce it as one.
          The wrapper's own role already makes its subtree presentational; saying so here
          keeps the badge correct if that wrapper is ever changed. */}
      <span className="inline-flex" aria-hidden="true">
        <StarRating value={rating.avg_rating} readOnly variant={variant} size="sm" />
      </span>
      <span className={`text-xs tabular-nums ${variant === "dark" ? "text-white/80" : "text-eh-ink"}`} aria-hidden="true">
        {avg}
      </span>
      <span className={`text-[11px] tabular-nums ${muted}`} aria-hidden="true">({count})</span>
    </span>
  );
}
